/**
 * Onglet « Analyses » — les cinq analyses explicatives.
 *
 * Une seule est chargée à la fois, choisie dans la query string (`?a=`). Les
 * cinq requêtes sont lourdes — cohortes, sessions, médianes par morceau — et les
 * lancer toutes pour n'en regarder qu'une gaspillerait des secondes de base à
 * chaque visite.
 *
 * Chaque analyse s'ouvre sur un encart « Lecture » : le verdict d'abord, la
 * méthode ensuite, les graphiques après. Ces cinq analyses mesurent toutes des
 * ASSOCIATIONS ; livrées sans phrase de lecture, elles se font interpréter comme
 * des effets.
 */

import { AnalysisNav, useSelectedAnalysis, type AnalysisId } from '../components/AnalysisNav';
import { ChurnPanel } from '../components/analyses/ChurnPanel';
import { CohortsPanel } from '../components/analyses/CohortsPanel';
import { CompletionPanel } from '../components/analyses/CompletionPanel';
import { FeaturesPanel } from '../components/analyses/FeaturesPanel';
import { ProgressionPanel } from '../components/analyses/ProgressionPanel';
import { SectionShell } from '../components/SectionShell';
import { useAnalysis } from '../hooks/usePeriodStats';
import { useAuth } from '../hooks/useAuth';
import { usePeriodParam } from '../hooks/usePeriodParam';
import type {
  ChurnAnalysis, CohortAnalysis, CompletionAnalysis, FeatureAnalysis, ProgressionAnalysis,
} from '../types';

export function AnalysesPage() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();
  const [selected] = useSelectedAnalysis();

  // Les cinq hooks sont appeles a chaque rendu — les regles des hooks
  // l'imposent — mais `enabled` n'en laisse partir qu'un seul. Sans ce
  // drapeau, ouvrir l'onglet declencherait les cinq requetes, dont quatre
  // inutiles et toutes couteuses.
  const optionsFor = (id: AnalysisId) => ({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
    enabled: selected === id,
  });

  const cohorts = useAnalysis<CohortAnalysis>('cohorts', optionsFor('cohorts'));
  const progression = useAnalysis<ProgressionAnalysis>('progression', optionsFor('progression'));
  const completion = useAnalysis<CompletionAnalysis>('completion', optionsFor('completion'));
  const churn = useAnalysis<ChurnAnalysis>('churn', optionsFor('churn'));
  const features = useAnalysis<FeatureAnalysis>('features', optionsFor('features'));

  const active = {
    cohorts,
    progression,
    completion,
    churn,
    features,
  }[selected];

  return (
    <SectionShell
      lastSyncAt={active.data?.meta.lastSyncAt}
      isPending={active.isPending}
      isFetching={active.isFetching}
      error={active.error}
      hasData={Boolean(active.data)}
      beforeContent={<AnalysisNav />}
    >
      {selected === 'cohorts' && cohorts.data ? <CohortsPanel data={cohorts.data.analysis} /> : null}
      {selected === 'progression' && progression.data ? (
        <ProgressionPanel data={progression.data.analysis} />
      ) : null}
      {selected === 'completion' && completion.data ? (
        <CompletionPanel data={completion.data.analysis} />
      ) : null}
      {selected === 'churn' && churn.data ? <ChurnPanel data={churn.data.analysis} /> : null}
      {selected === 'features' && features.data ? (
        <FeaturesPanel data={features.data.analysis} />
      ) : null}
    </SectionShell>
  );
}
